import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Truck, Plus, Search, Trash2, Edit3, ExternalLink, CheckCircle2, MessageCircle, AlertTriangle, Clock,
} from 'lucide-react';
import ErpShell from '../components/ErpShell';
import { ErpPageHeader, ErpEmpty } from '../components/ErpPageHeader';
import { api } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { todayStr } from '../lib/financeShared';

const CARRIERS = ['DHL', 'FEDEX', 'UPS', 'Gilron', 'Orian', 'ZIM', 'Maersk', 'MSC', 'Aramex', 'Other'];

const STATUS_META = {
  ON_THE_WAY:   { label: 'On the way',   tone: 'info' },
  ARRIVING_SOON:{ label: 'Arriving soon',tone: 'warning' },
  OVERDUE:      { label: 'Past ETA',     tone: 'danger' },
  ARRIVED:      { label: 'Arrived',      tone: 'emerald' },
  DELAYED:      { label: 'Delayed',      tone: 'danger' },
};

const emptyForm = () => ({
  customerName: '', phone: '', carrier: 'DHL', trackingNumber: '', trackingUrl: '',
  origin: '', destination: '', eta: '', leadDays: 5, status: 'ON_THE_WAY', notes: '',
});

const rowGlow = (s) => {
  if (s === 'OVERDUE' || s === 'DELAYED') return { boxShadow: 'inset 3px 0 0 var(--gg-danger)' };
  if (s === 'ARRIVING_SOON') return { boxShadow: 'inset 3px 0 0 var(--gg-warning, #FF9500)' };
  return {};
};

const waLink = (t) => {
  const phone = (t.phone || '').replace(/[^\d]/g, '');
  const msg = encodeURIComponent(
    `Hello${t.customerName ? ' ' + t.customerName : ''}, your shipment${t.trackingNumber ? ' ' + t.trackingNumber : ''}` +
    ` via ${t.carrier || 'carrier'} is arriving soon${t.eta ? ' (ETA ' + t.eta + ')' : ''}.` +
    (t.trackingUrl ? ` Track it here: ${t.trackingUrl}` : '')
  );
  return phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
};

export default function ErpTracking() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/tracking');
      setRows(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch { toast({ title: 'Failed to load', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((t) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [t.customerName, t.carrier, t.trackingNumber, t.origin, t.destination, t.notes]
      .some((v) => (v || '').toLowerCase().includes(s));
  }), [rows, q]);

  const openNew = () => { setForm(emptyForm()); setEditingId(null); setShowForm(true); };
  const openEdit = (t) => {
    setForm({ ...emptyForm(), ...t, leadDays: t.leadDays ?? 5 });
    setEditingId(t.id);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.customerName.trim()) { toast({ title: 'Customer name required', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      if (editingId) await api.put(`/tracking/${editingId}`, form);
      else await api.post('/tracking', form);
      toast({ title: editingId ? 'Shipment updated' : 'Shipment added to tracking' });
      setShowForm(false);
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed to save', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const markArrived = async (t) => {
    try { await api.put(`/tracking/${t.id}`, { ...t, status: 'ARRIVED' }); toast({ title: 'Marked arrived' }); load(); }
    catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };
  const remove = async (id) => {
    if (!window.confirm('Remove this shipment from tracking?')) return;
    try { await api.delete(`/tracking/${id}`); toast({ title: 'Removed' }); load(); }
    catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const etaLabel = (t) => {
    if (!t.eta) return '—';
    if (t.daysToEta === null || t.daysToEta === undefined) return t.eta;
    if (t.displayStatus === 'ARRIVED') return t.eta;
    if (t.daysToEta < 0) return `${t.eta} · ${Math.abs(t.daysToEta)}d late`;
    if (t.daysToEta === 0) return `${t.eta} · today`;
    return `${t.eta} · in ${t.daysToEta}d`;
  };

  return (
    <ErpShell title="Tracking">
      <ErpPageHeader
        eyebrow="Shipments in transit"
        title="Tracking"
        subtitle="Log shipments on the way, jump straight to the carrier's site, and get reminded before they arrive."
        icon={Truck}
        actions={
          <>
            <div className="relative">
              <Search size={14} className="absolute start-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--gg-text-dim)' }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, tracking…" className="gg-input ps-9" style={{ width: 230 }} data-testid="tracking-search" />
            </div>
            <button onClick={openNew} className="gg-btn-primary" data-testid="new-tracking-btn"><Plus size={14} /> Track Shipment</button>
          </>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="On the way" value={counts.onTheWay ?? 0} />
        <Stat label="Arriving soon" value={counts.arrivingSoon ?? 0} tone="warning" />
        <Stat label="Past ETA" value={(counts.overdue ?? 0) + (counts.delayed ?? 0)} tone="danger" />
        <Stat label="Arrived" value={counts.arrived ?? 0} tone="emerald" />
      </div>

      {loading ? (
        <div className="gg-card p-12 animate-pulse" />
      ) : filtered.length === 0 ? (
        <ErpEmpty icon={Truck} title={q ? 'No shipments match' : 'Nothing being tracked yet'} subtitle="Add a shipment a customer told you about — you'll be reminded before it arrives." action={<button onClick={openNew} className="gg-btn-primary"><Plus size={14} /> Track Shipment</button>} />
      ) : (
        <div className="gg-card overflow-hidden" data-testid="tracking-table">
          <div className="overflow-x-auto">
            <table className="gg-table" style={{ minWidth: 960 }}>
              <thead>
                <tr>
                  <th>Customer</th><th>Carrier</th><th>Tracking #</th><th>Route</th><th>ETA</th><th>Status</th>
                  <th style={{ width: 200 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t, idx) => {
                  const meta = STATUS_META[t.displayStatus] || STATUS_META.ON_THE_WAY;
                  return (
                    <motion.tr key={t.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: Math.min(idx * 0.015, 0.2) }} style={rowGlow(t.displayStatus)} data-testid={`tracking-row-${t.id}`}>
                      <td>
                        <p className="font-semibold">{t.customerName || '—'}</p>
                        <p className="text-xs" style={{ color: 'var(--gg-text-dim)' }}>{t.phone || ''}</p>
                      </td>
                      <td>{t.carrier ? <span className="gg-chip gg-chip-info">{t.carrier}</span> : '—'}</td>
                      <td className="gg-tabular">
                        {t.trackingUrl ? (
                          <a href={t.trackingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold" style={{ color: 'var(--gg-accent)' }} data-testid={`track-link-${t.id}`}>
                            {t.trackingNumber || 'Open'} <ExternalLink size={11} />
                          </a>
                        ) : (t.trackingNumber || '—')}
                      </td>
                      <td className="text-xs" style={{ color: 'var(--gg-text-secondary)' }}>{[t.origin, t.destination].filter(Boolean).join(' → ') || '—'}</td>
                      <td className="gg-tabular text-xs" style={{ color: t.displayStatus === 'OVERDUE' ? 'var(--gg-danger)' : t.displayStatus === 'ARRIVING_SOON' ? 'var(--gg-warning, #FF9500)' : 'var(--gg-text-secondary)' }}>
                        {(t.displayStatus === 'ARRIVING_SOON' || t.displayStatus === 'OVERDUE') && <Clock size={11} className="inline me-1" />}
                        {etaLabel(t)}
                      </td>
                      <td><span className={`gg-chip gg-chip-${meta.tone}`}>{meta.label}</span></td>
                      <td>
                        <div className="flex gap-1 justify-end">
                          {t.trackingUrl && (
                            <a href={t.trackingUrl} target="_blank" rel="noreferrer" className="gg-btn-ghost" style={{ color: 'var(--gg-accent)' }} title="Open tracking site" data-testid={`open-track-${t.id}`}><ExternalLink size={13} /></a>
                          )}
                          {(t.displayStatus === 'ARRIVING_SOON' || t.displayStatus === 'OVERDUE') && (
                            <a href={waLink(t)} target="_blank" rel="noreferrer" className="gg-btn-ghost" style={{ color: '#25D366' }} title="WhatsApp reminder" data-testid={`wa-track-${t.id}`}><MessageCircle size={13} /></a>
                          )}
                          {t.displayStatus !== 'ARRIVED' && (
                            <button onClick={() => markArrived(t)} className="gg-btn-ghost" style={{ color: 'var(--gg-emerald)' }} title="Mark arrived" data-testid={`arrived-${t.id}`}><CheckCircle2 size={13} /></button>
                          )}
                          <button onClick={() => openEdit(t)} className="gg-btn-ghost" data-testid={`edit-track-${t.id}`}><Edit3 size={13} /></button>
                          <button onClick={() => remove(t.id)} className="gg-btn-ghost" style={{ color: 'var(--gg-danger)' }} data-testid={`del-track-${t.id}`}><Trash2 size={13} /></button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="gg-cmdk-overlay flex items-start justify-center pt-14 overflow-y-auto" onClick={() => !saving && setShowForm(false)}>
          <motion.div initial={{ opacity: 0, y: 10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} onClick={(e) => e.stopPropagation()} className="gg-cmdk-panel p-6 mb-10" style={{ width: 'min(680px, 94vw)', maxHeight: '86vh', overflowY: 'auto' }} data-testid="tracking-form">
            <p className="gg-eyebrow" style={{ color: 'var(--gg-accent)' }}>{editingId ? 'Edit' : 'New'}</p>
            <h2 className="gg-hero-title" style={{ fontSize: 24 }}>{editingId ? 'Update' : 'Track'} Shipment</h2>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <F label="Customer Name *"><input value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} className="gg-input" data-testid="track-customer" /></F>
              <F label="Phone (for WhatsApp)"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="gg-input" data-testid="track-phone" /></F>
              <F label="Carrier">
                <input value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} className="gg-input" list="carrier-list" data-testid="track-carrier" />
                <datalist id="carrier-list">{CARRIERS.map((c) => <option key={c} value={c} />)}</datalist>
              </F>
              <F label="Tracking Number"><input value={form.trackingNumber} onChange={(e) => setForm({ ...form, trackingNumber: e.target.value })} className="gg-input" data-testid="track-number" /></F>
              <div className="col-span-2">
                <F label="Tracking Link (carrier website URL)"><input value={form.trackingUrl} onChange={(e) => setForm({ ...form, trackingUrl: e.target.value })} className="gg-input" placeholder="https://www.dhl.com/track?..." data-testid="track-url" /></F>
              </div>
              <F label="Origin"><input value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} className="gg-input" data-testid="track-origin" /></F>
              <F label="Destination"><input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className="gg-input" data-testid="track-destination" /></F>
              <F label="Expected arrival (ETA)"><input type="date" value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} className="gg-input" data-testid="track-eta" /></F>
              <F label="Remind me days before">
                <input type="number" min="0" value={form.leadDays} onChange={(e) => setForm({ ...form, leadDays: e.target.value })} className="gg-input" data-testid="track-lead" />
              </F>
              <F label="Status">
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="gg-input" data-testid="track-status">
                  <option value="ON_THE_WAY">On the way</option>
                  <option value="DELAYED">Delayed</option>
                  <option value="ARRIVED">Arrived</option>
                </select>
              </F>
              <div className="col-span-2">
                <F label="Notes"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="gg-input" rows={2} style={{ height: 'auto', padding: '10px 14px' }} data-testid="track-notes" /></F>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowForm(false)} disabled={saving} className="gg-btn-secondary" data-testid="track-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="gg-btn-primary" data-testid="track-save">{saving ? 'Saving…' : editingId ? 'Update' : 'Save'}</button>
            </div>
          </motion.div>
        </div>
      )}
    </ErpShell>
  );
}

const Stat = ({ label, value, tone }) => (
  <div className="gg-card p-4">
    <p className="gg-eyebrow">{label}</p>
    <p className="gg-tabular mt-1" style={{ fontSize: 24, fontWeight: 700, color: tone === 'emerald' ? 'var(--gg-emerald)' : tone === 'danger' ? 'var(--gg-danger)' : tone === 'warning' ? 'var(--gg-warning, #FF9500)' : 'var(--gg-text)' }}>{value}</p>
  </div>
);

const F = ({ label, children }) => (
  <div><label className="gg-eyebrow block mb-1">{label}</label>{children}</div>
);
